import { styled, css } from "styled-components";

import Link from "components/Link";
import { Container as NewsPageSectionContainer } from "components/NewsPageSection/NewsPageSection.styled";
import TextBlock from "components/TextBlock";
import { Container as GridContainer } from "style/components/Page";
import {
    styleBodyL,
    styleBodyLAlt,
    styleHeadingS,
    styleHeadingXL,
    styleHeadingXXL,
    styleLabelM,
    styleLabelS,
} from "style/components/Typography";
import { columnGap } from "style/grid";
import { topPad } from "style/headerOffsets";
import { MQ } from "style/mediaQueries";

export const Container = styled(GridContainer)`
    ${columnGap};
    display: grid;
    grid-auto-rows: auto;
    grid-template-columns: repeat(4, [col-start] 1fr);
    row-gap: 2.1875rem;

    ${MQ.FROM_M} {
        grid-template-columns: repeat(12, [col-start] 1fr);
    }

    ${MQ.FROM_XL} {
        row-gap: 56px;
    }
`;

export const PreHeading = styled.div`
    ${styleLabelS};
    margin-bottom: 0.4375rem;
`;

export const Header = styled.h1.attrs<{
    $headingSize: string;
}>(() => ({}))`
    ${(props) =>
        props.$headingSize === "XXL"
            ? css`
                  ${styleHeadingXXL}
              `
            : css`
                  ${styleHeadingXL}
              `};
    grid-column: col-start 1 / span 4;
    margin: 0;

    ${MQ.FROM_M} {
        grid-column: col-start 1 / span 12;
    }
`;

export const Information = styled.div`
    ${columnGap};
    align-content: flex-start;
    display: grid;
    grid-auto-rows: auto;
    grid-column: col-start 1 / span 4;
    grid-template-columns: repeat(4, [col-start] 1fr);

    ${MQ.FROM_XL} {
        grid-column: col-start 1 / span 5;
        grid-template-columns: repeat(5, [col-start] 1fr);
    }
`;

export const Category = styled.div`
    ${styleLabelM};
    grid-column: col-start 1 / span 4;
    margin-bottom: 1.75rem;

    ${MQ.FROM_XL} {
        grid-column: col-start 1 / span 5;
    }
`;

export const CategoryLink = styled(Link)`
    ${styleLabelM};

    span {
        border-bottom-color: transparent;

        &:hover {
            border-bottom-color: currentcolor;
        }
    }
`;

export const StartPublish = styled.div`
    ${styleLabelS};
    grid-column: col-start 1 / span 2;
`;

export const Length = styled.div`
    ${styleLabelS};
    grid-column: col-start 3 / span 2;

    ${MQ.FROM_XL} {
        grid-column: col-start 3 / span 3;
    }
`;

const introductionStyle = css`
    grid-column: col-start 1 / span 4;

    ${MQ.FROM_M} {
        grid-column: col-start 5 / span 8;
    }

    ${MQ.FROM_L} {
        grid-column: col-start 5 / span 7;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 6 / span 6;
    }
`;

export const Introduction = styled.div`
    ${styleBodyLAlt};
    ${introductionStyle}
`;

export const IntroductionHtml = styled(TextBlock)`
    ${styleBodyL}
    ${introductionStyle}

    & > div > div > p:first-child {
        ${styleBodyLAlt}
    }
`;

export const Authors = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
    grid-column: col-start 1 / span 4;
    margin-top: calc(0.875rem - 2.1875rem);

    ${MQ.FROM_M} {
        grid-column: col-start 5 / span 8;
        margin-top: calc(1.3125rem - 2.1875rem);
    }

    ${MQ.FROM_L} {
        grid-column: col-start 5 / span 7;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 6 / span 6;
        margin-top: calc(1.75rem - 3.5rem);
    }
`;

export const TextContent = styled.div`
    grid-column: col-start 1 / span 4;

    ${MQ.FROM_M} {
        grid-column: col-start 5 / span 8;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 6 / span 7;
    }
`;

export const MainContentWrapper = styled.div`
    ${columnGap};
    display: grid;
    grid-auto-rows: auto;
    grid-column: col-start 1 / span 4;
    grid-template-columns: repeat(4, [col-start] 1fr);
    position: relative;
    row-gap: 2.1875rem;

    ${MQ.FROM_M} {
        grid-column: col-start 1 / span 12;
        grid-template-columns: repeat(12, [col-start] 1fr);
    }

    ${MQ.FROM_XL} {
        row-gap: 3.5rem;
    }
`;

export const MainContent = styled.div`
    display: flex;
    flex-direction: column;
    grid-column: col-start 1 / span 4;
    margin-top: 2.625rem;
    row-gap: 2.1875rem;

    ${MQ.FROM_M} {
        grid-column: col-start 5 / span 8;
        row-gap: 2.625rem;
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 6 / span 7;
        row-gap: 3.5rem;
    }

    & > div {
        &:not(:first-child) {
            ${NewsPageSectionContainer} {
                border-top: 1px solid var(--line-on-neutral-default);
                margin-top: calc(2.625rem - 2.1875rem);
                padding-top: 2.625rem;

                ${MQ.FROM_M} {
                    margin-top: calc(4.375rem - 2.625rem);
                    padding-top: 4.375rem;
                }

                ${MQ.FROM_XL} {
                    margin-top: calc(6.125rem - 3.5rem);
                    padding-top: 6.125rem;
                }
            }
        }
    }
`;

export const Navigation = styled.div.attrs<{
    $headerVisible: boolean;
}>(() => ({}))`
    ${topPad}
    align-self: start;
    background: var(--background-default);
    grid-column: col-start 1 / span 4;
    margin: 0 -1.75rem;
    overflow-x: auto;
    position: sticky;
    transition: top 0.2s ease-in-out;
    z-index: 1;

    &::after {
        background: var(--overlay-neutral-darken);
        content: "";
        inset: 0;
        position: absolute;
        z-index: -1;
    }

    ${MQ.FROM_M} {
        background: none;
        grid-column: col-start 1 / span 4;
        margin: 0;

        &::after {
            display: none;
        }
    }

    ${MQ.FROM_XL} {
        grid-column: col-start 4 / span 2;
    }
`;

export const NavigationLinkWrapper = styled.div`
    display: flex;
    flex-flow: row nowrap;
    gap: 0.875rem;
    overflow-x: auto;
    padding: 1.3125rem 1.75rem;

    span {
        white-space: nowrap;
    }

    ${MQ.FROM_M} {
        flex-flow: column wrap;
        overflow-x: auto;
        padding: 2.625rem 0 0;

        span {
            white-space: normal;
        }
    }
`;

export const NavigationLink = styled(Link).attrs<{
    $active: boolean;
}>(() => ({}))`
    ${styleHeadingS};
    background: none;
    border: none;
    color: var(--text-on-neutral-secondary);
    cursor: pointer;
    padding: 0;
    text-align: left;

    span {
        border-bottom: 1px solid var(--color-black-o00);
        transition:
            border-color 0.2s ease-in-out,
            color 0.2s ease-in-out;
    }

    ${MQ.FROM_M} {
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
    }

    ${(props) =>
        props.$active &&
        css`
            color: var(--text-on-neutral-primary);
        `}

    &:hover {
        span {
            border-bottom: 1px solid var(--text-on-neutral-secondary);
        }
    }

    &:focus-visible {
        outline-offset: 0;
    }
`;
